import { useCallback, useContext, useEffect, useState } from 'react';
import { DICTONARY } from 'const/Dictonary';
import empresasService from 'services/empresas';
import { AuthContext } from 'context';
import { PERMISOS } from 'const/Permisos';
import { limpiaParametrosVacios } from 'helpers/manejoLLamadasServicios';

export const useIndicadores = codigoEmpresa => {
  const {
    perfiles: { isSuperAdmin },
    isTienePermisos
  } = useContext(AuthContext);
  // Sucursales
  const [isLoading, setIsLoading] = useState(true);
  const [sucursales, setSucursales] = useState([]);
  // Empresas
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [isLoadingEmpresas, setIsLoadingEmpresas] = useState(false);
  // Grupos
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [isLoadingGrupos, setIsLoadingGrupos] = useState(false);

  // Solo para usuarios con permisos de administración de sucursales
  /**
   * Cargar la lista de sucursales
   * @function cargarListaSucursales
   * @params
   * - codigoEmpresa: Opcional. Se usa para filtro de datos. *
   * - codigoGrupo: Opcional. Se usa para filtro de datos.
   */
  const cargarListaSucursales = useCallback(async () => {
    if (
      !isTienePermisos(PERMISOS.administracionSucursales) &&
      !empresaSeleccionada &&
      !grupoSeleccionado
    ) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await empresasService.obtenerListaSucursales({
        codigoEmpresa: empresaSeleccionada?.codigoEmpresa,
        codigoGrupo: grupoSeleccionado?.codigoGrupo || null,
        asociados: isSuperAdmin ? false : true //si es super admin false para obtener todo, si no sucursales con permiso
      });

      // console.log('response', response.config.params);

      if (response.data.error) {
        throw new Error('ERROR_AL_CARGAR_SUCURSALES');
      }
      const listaSucursales = response.data;

      setSucursales(listaSucursales);
    } catch (error) {
      console.error('Error al obtener las sucursales:', error);
      setSucursales([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    empresaSeleccionada,
    codigoEmpresa,
    isTienePermisos,
    grupoSeleccionado,
    isSuperAdmin
  ]);

  /**
   * Cargar las empresas asociadas al grupo seleccionado
   * @function cargarListaEmpresas
   * @params
   *
   * obtenerListaEmpresas
   *  - codigoGrupo: Opcional. Se usa para filtro de datos. *
   *  - nombreEmpresa: Opcional. Se usa para filtro de datos. *
   *  - asociados: true o false. Se usa para indicar si las empresas deben estar filtradas por las que el usuario tiene permiso en sus sucursales.
   */
  const cargarListaEmpresas = useCallback(async () => {
    setIsLoadingEmpresas(true);

    const params = {
      codigoGrupo: grupoSeleccionado?.codigoGrupo,
      asociados: !isSuperAdmin // Indica que las empresas deben estar filtradas por las que el usuario tiene permiso en sus sucursales.
    };
    // Filtrar parámetros nulos
    const filteredParams = limpiaParametrosVacios(params);

    try {
      const response = await empresasService.obtenerListaEmpresas(
        filteredParams
      );
      // console.log('response', response);
      if (response.data.error) {
        throw new Error('ERROR_AL_CARGAR_EMPRESAS');
      }
      const empresasAux = response.data.map(empresa => ({
        codigoEmpresa: empresa.codigoEmpresa,
        nombreEmpresa: empresa.nombreEmpresa
      }));

      setEmpresas(empresasAux);
    } catch (error) {
      console.error('Error al cargar las empresas:', error);
      setEmpresas([]);
    } finally {
      setIsLoadingEmpresas(false);
    }
  }, [grupoSeleccionado?.codigoGrupo, isSuperAdmin]);

  const cargarListaGrupos = useCallback(async () => {
    setIsLoadingGrupos(true);

    try {
      const response = await empresasService.obtenerListaGrupos();

      if (response.data.error) {
        throw new Error('ERROR_AL_CARGAR_GRUPOS');
      }
      setGrupos(response.data);
    } catch (error) {
      console.error('Error al cargar los grupos:', error);
      setGrupos([]);
    } finally {
      setIsLoadingGrupos(false);
    }
  }, []);

  //  Como funcionaba antes
  const cargarSucursales = async () => {
    setIsLoading(true);
    let listaSucursales = [];

    try {
      if (isNaN(codigoEmpresa) && isNaN(empresaSeleccionada?.codigoEmpresa)) {
        setIsLoading(false);
        return;
      }

      const response = await empresasService.obtieneSucursales({
        codigoEmpresa: empresaSeleccionada
          ? empresaSeleccionada.codigoEmpresa
          : codigoEmpresa,
        totalPagina: DICTONARY.PAGINACION.ALL_DATA
      });

      const error = response.data.error;
      if (error) throw new Error('ERROR_AL_CARGAR_SUCURSALES');

      listaSucursales = response.data.content;

      const promises = listaSucursales.map(sucursal => {
        return empresasService
          .getSucursal(sucursal.codigoSucursal)
          .then(responseSucursal => {
            return responseSucursal && responseSucursal.data
              ? responseSucursal.data
              : null;
          });
      });

      Promise.all(promises)
        .then(sucursales => {
          setSucursales(sucursales);
          setIsLoading(false);
        })
        .catch(error => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
      setSucursales([]);
      console.log('Error al obtener las sucursales ', error);
    }
  };

  useEffect(() => {
    if (
      empresaSeleccionada &&
      empresaSeleccionada.codigoEmpresa === codigoEmpresa &&
      sucursales.length > 0
    ) {
      return;
    }
    isTienePermisos(PERMISOS.administracionSucursales) ||
    isTienePermisos(PERMISOS.sucursales)
      ? cargarListaSucursales()
      : cargarSucursales();
  }, [empresaSeleccionada, grupoSeleccionado]);

  useEffect(() => {
    if (
      isTienePermisos(PERMISOS.administracionSucursales) ||
      isTienePermisos(PERMISOS.sucursales)
    ) {
      cargarListaEmpresas();
    }
  }, []);

  useEffect(() => {
    if (
      isTienePermisos(PERMISOS.administracionSucursales) ||
      isTienePermisos(PERMISOS.sucursales)
    ) {
      cargarListaGrupos();
    }
  }, []);

  return {
    isLoading,
    isSuperAdmin,
    isLoadingEmpresas,
    isLoadingGrupos,
    // Sucursales
    sucursales,
    setSucursales,
    // Empresas
    empresas,
    empresaSeleccionada,
    setEmpresaSeleccionada,
    cargarListaEmpresas,
    // Grupos
    grupos,
    grupoSeleccionado,
    setGrupoSeleccionado
  };
};
